import { useMemo, useCallback } from 'react';
import { useDatabase } from '@/context/DatabaseContext';
import { useAppStore } from '@/store/appStore';
import { useSpaces } from './useSpaces';
import { useTaskEnrichment } from './useTaskEnrichment';
import { applyTaskFilters } from '@/utils/taskFilters';
import { useOrgMembers } from './useOrgMembers';
import type { Task } from '@/types';

/**
* Hook base per tutte le view che lavorano con tasks
* Fornisce la logica comune: tasks arricchiti, filtri, grouping, sorting, etc.
*/
export function useViewData() {
  const { tasks: allTasks, loading } = useDatabase();
  const { containersMap } = useSpaces();

  // Filter tasks to only include those belonging to current org's spaces
  const tasks = useMemo(() => {
    if (!containersMap || containersMap.size === 0) return allTasks;
    return allTasks.filter(t => containersMap.has(t.spaceId));
  }, [allTasks, containersMap]);

  const { enrichedTasks, uniqueValues } = useTaskEnrichment({
  tasks,
  containersMap: containersMap || new Map(),
  });

  const filters = useAppStore((state) => state.filters);
  const grouping = useAppStore((state) => state.grouping);
  const sorting = useAppStore((state) => state.sorting);
  const viewMode = useAppStore((state) => state.viewMode);
  const setFilters = useAppStore((state) => state.setFilters);
  const setGrouping = useAppStore((state) => state.setGrouping);
  const setSorting = useAppStore((state) => state.setSorting);
  const selectTask = useAppStore((state) => state.selectTask);
  const navigate = useAppStore((state) => state.navigate);
  const setViewMode = useAppStore((state) => state.setViewMode);

  const { orgMemberNames, availableAssignees, resolveDisplayName } = useOrgMembers(uniqueValues.assignees);

  const filteredTasks = useMemo(() => {
  return applyTaskFilters(enrichedTasks, filters, containersMap || undefined, resolveDisplayName);
  }, [enrichedTasks, filters, containersMap, resolveDisplayName]);

  const handleTaskClick = useCallback((task: Task) => {
  selectTask(task.id);
  }, [selectTask]);

  return {
  loading,
  tasks: filteredTasks,
  enrichedTasks,
  filters,
  grouping,
  sorting,
  viewMode,
  uniqueValues,
  availableAssignees,
  orgMemberNames,
  resolveDisplayName,
  availableTags: uniqueValues.tags,
  currentPath: filters.path,
  containersMap,
  onTaskClick: handleTaskClick,
  onFilterChange: setFilters,
  onGroupingChange: setGrouping,
  onSortingChange: setSorting,
  onNavigate: navigate,
  onViewModeChange: setViewMode,
  };
}
